import {
	type MeetStatusCount,
	RESULT_STATUSES,
	type ResultStatus,
	SCHEDULE_STATUSES,
	type ScheduleStatus,
} from "@seagames/contracts";

/**
 * The dashboard's status groups — what an operator asks of the programme:
 * what is live, what waits to be made official, what is ready (start list),
 * what is still ahead, what is on hold, what is done, what was called off.
 *
 * A meet has TWO statuses (when / how far the result got); a group is decided
 * from both, in this order:
 *
 *   1. result OFFICIAL                  → official   (a final result is final, whatever the schedule says)
 *   2. schedule CANCELLED               → cancelled  (will not be played; not counted as work to do)
 *   3. schedule INTERRUPTED / POSTPONED → on_hold    (stopped or moved — someone must act before it resumes)
 *   4. otherwise by the result:         LIVE → live, UNOFFICIAL → unofficial,
 *                                       START_LIST → start_list, NONE → scheduled
 *      (TENTATIVE, SCHEDULED and DELAYED meets are all still "running" to plan.)
 *
 * Every group is a product of a schedule-status list and a result-status
 * list, so `groupFilter` turns it into ONE `GET /meets` filter that lists
 * exactly the meets the strip counted (the unit test proves the partition).
 *
 * The statuses come from `@seagames/contracts` only (CLAUDE.md, bug 4).
 */
export const STATUS_GROUPS = ["live", "unofficial", "start_list", "scheduled", "on_hold", "official", "cancelled"] as const;
export type StatusGroup = (typeof STATUS_GROUPS)[number];

export const isStatusGroup = (v: unknown): v is StatusGroup => typeof v === "string" && (STATUS_GROUPS as readonly string[]).includes(v);

const CANCELLED: readonly ScheduleStatus[] = ["CANCELLED"] satisfies readonly ScheduleStatus[];
const ON_HOLD: readonly ScheduleStatus[] = ["INTERRUPTED", "POSTPONED"] satisfies readonly ScheduleStatus[];
/** Everything else on the schedule — a new schedule status lands here until someone decides otherwise. */
const RUNNING: readonly ScheduleStatus[] = SCHEDULE_STATUSES.filter((s) => !CANCELLED.includes(s) && !ON_HOLD.includes(s));
const NOT_OFFICIAL: readonly ResultStatus[] = RESULT_STATUSES.filter((r) => r !== "OFFICIAL");

const BY_RESULT: Record<Exclude<ResultStatus, "OFFICIAL">, StatusGroup> = {
	NONE: "scheduled",
	START_LIST: "start_list",
	LIVE: "live",
	UNOFFICIAL: "unofficial",
};

/** The one group a meet belongs to. */
export function statusGroupOf(schedule: ScheduleStatus, result: ResultStatus): StatusGroup {
	if (result === "OFFICIAL") return "official";
	if (CANCELLED.includes(schedule)) return "cancelled";
	if (ON_HOLD.includes(schedule)) return "on_hold";
	return BY_RESULT[result];
}

/** `GET /meets` status filters that list exactly one group. */
export interface GroupFilter {
	schedule_status?: readonly ScheduleStatus[];
	result_status: readonly ResultStatus[];
}

export function groupFilter(group: StatusGroup): GroupFilter {
	switch (group) {
		case "official":
			return { result_status: ["OFFICIAL"] };
		case "cancelled":
			return { schedule_status: CANCELLED, result_status: NOT_OFFICIAL };
		case "on_hold":
			return { schedule_status: ON_HOLD, result_status: NOT_OFFICIAL };
		case "live":
			return { schedule_status: RUNNING, result_status: ["LIVE"] };
		case "unofficial":
			return { schedule_status: RUNNING, result_status: ["UNOFFICIAL"] };
		case "start_list":
			return { schedule_status: RUNNING, result_status: ["START_LIST"] };
		case "scheduled":
			return { schedule_status: RUNNING, result_status: ["NONE"] };
	}
}

export interface GroupTotals {
	/** Every meet counted. The groups add up to it. */
	total: number;
	/** The meets that will be played: `total` minus the cancelled ones — the "M" of "N of M official". */
	toPlay: number;
	byGroup: Record<StatusGroup, number>;
}

const zeroGroups = (): Record<StatusGroup, number> =>
	Object.fromEntries(STATUS_GROUPS.map((g) => [g, 0])) as Record<StatusGroup, number>;

/** `GET /meets/status-counts` rows (or one sport's `counts`) → meets per group. Pure; never counts meets itself. */
export function groupTotals(rows: readonly MeetStatusCount[]): GroupTotals {
	const byGroup = zeroGroups();
	let total = 0;
	for (const r of rows) {
		byGroup[statusGroupOf(r.schedule_status, r.result_status)] += r.count;
		total += r.count;
	}
	return { total, toPlay: total - byGroup.cancelled, byGroup };
}

/** Share of the meets to play that are official, 0–1 (0 when there is nothing to play). */
export const officialShare = (t: GroupTotals): number => (t.toPlay > 0 ? t.byGroup.official / t.toPlay : 0);

/**
 * The share as a whole percent for the headline, rounded DOWN: 5,909 of 5,929
 * is "99 %", never "100 %" while 20 meets still wait. (Returns 0–1, for Intl's percent style.)
 */
export const shareForDisplay = (share: number): number => Math.floor(share * 100 + 1e-9) / 100;

/** The stacked bar, left to right: done first, then the work in hand; "scheduled" is the empty track. */
export const BAR_GROUPS = ["official", "unofficial", "live", "start_list", "on_hold"] as const satisfies readonly StatusGroup[];
